<template>
  <div class="flex min-h-dvh flex-col bg-page">
    <ScheduledDeletionGate>
      <component :is="layout">
        <!-- This will render the current route component -->
        <router-view />
      </component>
    </ScheduledDeletionGate>
  </div>
  <PvToast />
  <PvToast group="admin-undo" position="top-right">
    <template #container="{ message, closeCallback }">
      <div class="flex w-full items-start gap-4 p-4">
        <div
          class="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-blue"
        >
          <IconNotificationsActive class="text-base text-white" />
        </div>
        <div class="flex min-w-0 flex-1 flex-col gap-3">
          <div class="flex flex-col gap-1">
            <p
              class="text-base/snug font-medium tracking-admin-snug text-white"
            >
              {{ message.summary }}
            </p>
            <p
              v-if="message.detail"
              class="text-sm/snug tracking-admin-snug text-secondary-white"
            >
              {{ message.detail }}
            </p>
          </div>
          <button
            v-if="getUndoAction(message)"
            class="w-fit rounded-lg border border-lines px-4 py-2 text-sm leading-none font-medium tracking-admin-snug text-white hover:bg-white/10"
            @click="getUndoAction(message)?.().then(() => closeCallback())"
          >
            {{ $t("admin_common.undo") }}
          </button>
        </div>
      </div>
    </template>
  </PvToast>
  <ConfirmDialog />
</template>

<script setup lang="ts">
import ConfirmDialog from "primevue/confirmdialog";
import PvToast from "primevue/toast";
import ScheduledDeletionGate from "shared-ui/components/account/ScheduledDeletionGate.vue";
import { useCrisp } from "shared-ui/composables/use-crisp.ts";
import { usePostHog } from "shared-ui/composables/use-posthog.ts";
import { getUndoAction } from "shared-ui/composables/use-undo-toast";
import { useI18nRuntime } from "shared-ui/services/vue-i18n.ts";
import {
  useGoogleWorkspaceUsersStore,
  useIntegrationStore,
  useMicrosoft365UsersStore,
  useMicrosoftDriveSettingsStore,
} from "shared-ui/stores";
import {
  type AuthStore,
  AuthStoreInjectKey,
} from "shared-ui/types/auth-store.ts";
import {
  type IntegrationStoreActions,
  IntegrationStoreInjectKey,
  type IntegrationStoreState,
} from "shared-ui/types/integration-store.ts";
import {
  GoogleIntegrationUsersStoreInjectKey,
  type IntegrationUsersStore,
  MicrosoftIntegrationUsersStoreInjectKey,
} from "shared-ui/types/integration-users-store.ts";
import {
  type MainStoreActions,
  MainStoreInjectKey,
  type MainStoreState,
} from "shared-ui/types/main-store.ts";
import type { MicrosoftIntegrationStore } from "shared-ui/types/microsoft-integration-store.ts";
import {
  type AllowListSettingsStoreActions,
  GoogleWorkspaceAllowListStoreInjectKey,
  MicrosoftAllowListStoreInjectKey,
} from "shared-ui/types/sharing-allow-list-store.ts";
import {
  type WordpressIntegrationStore,
  WordpressIntegrationStoreInjectKey,
} from "shared-ui/types/wordpress-integration-store.ts";
import { onAnalyticsConsentChange } from "shared-ui/utils/cookie-consent.ts";
import { createCookieConsentLanguageUpdater } from "shared-ui/utils/cookie-consent-language.ts";
import { createCookieConsentConfig } from "shared-ui/utils/create-cookie-consent-config.ts";
import { loadCookieConsentSafely } from "shared-ui/utils/load-cookie-consent.ts";
import { inject, onMounted, provide, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useAuthStore } from "@/stores/auth-store.ts";
import { useGoogleWorkspaceSettingsStore } from "@/stores/google-workspace-settings-store.ts";
import { useMainStore } from "@/stores/main-store";
import { useWordpressIntegrationStore } from "@/stores/wordpress-integration-store.ts";
import IconNotificationsActive from "~icons/material-symbols/notifications-active-outline";

const { t, locale } = useI18n();
const { ensureLocaleNamespaces } = useI18nRuntime();
let updateConsentLanguage: ((language: string) => Promise<void>) | undefined;

watch(locale, async (language) => {
  await updateConsentLanguage?.(language);
});

const layout = inject("app:layout");
provide<MainStoreActions & MainStoreState>(MainStoreInjectKey, useMainStore());
provide<IntegrationStoreState & IntegrationStoreActions>(
  IntegrationStoreInjectKey,
  useIntegrationStore(),
);
provide<AuthStore>(AuthStoreInjectKey, useAuthStore());
provide<IntegrationUsersStore>(
  GoogleIntegrationUsersStoreInjectKey,
  useGoogleWorkspaceUsersStore(),
);
provide<MicrosoftIntegrationStore>(
  MicrosoftIntegrationUsersStoreInjectKey,
  useMicrosoft365UsersStore(),
);
provide<AllowListSettingsStoreActions>(
  GoogleWorkspaceAllowListStoreInjectKey,
  useGoogleWorkspaceSettingsStore(),
);
provide<WordpressIntegrationStore>(
  WordpressIntegrationStoreInjectKey,
  useWordpressIntegrationStore(),
);
provide<AllowListSettingsStoreActions>(
  MicrosoftAllowListStoreInjectKey,
  useMicrosoftDriveSettingsStore(),
);

async function loadCookieConsent(): Promise<void> {
  const [, , CookieConsent] = await Promise.all([
    usePostHog("on_reject"),
    import("shared-ui/assets/cookie-consent.css"),
    import("vanilla-cookieconsent"),
  ]);

  const cookieConsentOptions = createCookieConsentConfig(
    (key, language) => t(key, {}, { locale: language }),
    locale.value,
    window.location.hostname,
    (language) => `https://www.controla.ai/${language}/privacy-policy`,
    (language) => ensureLocaleNamespaces(language, ["common"]),
  );
  await CookieConsent.run(cookieConsentOptions);
  updateConsentLanguage = createCookieConsentLanguageUpdater(
    CookieConsent.setLanguage,
  );
  await updateConsentLanguage(locale.value);

  // GDPR: Crisp chat requires consent before loading
  const { loadCrisp } = useCrisp();
  loadCrisp();
}

function scheduleCookieConsent(): void {
  const load = (): void => void loadCookieConsentSafely(loadCookieConsent);
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(load, { timeout: 2000 });
  } else {
    setTimeout(load, 0);
  }
}

onMounted(() => {
  const authStore = useAuthStore();
  onAnalyticsConsentChange((analyticsAccepted) => {
    if (analyticsAccepted) {
      void authStore.syncPostHogIdentity(true).catch(() => undefined);
    }
  });

  if (document.readyState === "complete") {
    scheduleCookieConsent();
    return;
  }

  window.addEventListener("load", scheduleCookieConsent, { once: true });
});
</script>
